
import { supabase } from '@/lib/supabase';
import { Signal, SignalAccountStatus, PaginatedResponse } from '@/types/signal';

export const fetchBotSignals = async (
  botId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponse<Signal>> => {
  // Calculate the range start and end for pagination
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  
  // First get the total count
  const { count, error: countError } = await supabase
    .from('signals')
    .select('*', { count: 'exact', head: true })
    .eq('bot_id', botId);
  
  if (countError) throw countError;
  
  // Then get the paginated data
  const { data, error } = await supabase
    .from('signals')
    .select('*')
    .eq('bot_id', botId)
    .order('created_at', { ascending: false })
    .range(from, to);
  
  if (error) throw error;
  
  return {
    data: data || [],
    total: count || 0,
    page,
    pageSize
  };
};

export const fetchCTraderSignalStatuses = async (
  botId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponse<SignalAccountStatus>> => {
  // Calculate the range start and end for pagination
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  
  // First, get the signals for this bot
  const { data: signals, error: signalsError } = await supabase
    .from('signals')
    .select('id')
    .eq('bot_id', botId)
    .order('created_at', { ascending: false });
  
  if (signalsError) throw signalsError;
  if (!signals || signals.length === 0) 
    return { data: [], total: 0, page, pageSize };
  
  // Get the signal IDs
  const signalIds = signals.map(signal => signal.id);
  
  // Get the total count
  const { count, error: countError } = await supabase
    .from('signal_account_status')
    .select('*', { count: 'exact', head: true })
    .in('signal_id', signalIds);
  
  if (countError) throw countError;
  
  // Get the paginated signal statuses for these signals with related account information
  const { data, error } = await supabase
    .from('signal_account_status')
    .select(`
      *,
      trading_accounts:trading_account_id (number_account_trading),
      api_connections:api_connection_id (
        csp_accounts (csp_account_name)
      )
    `)
    .in('signal_id', signalIds)
    .order('created_at', { ascending: false })
    .range(from, to);
  
  if (error) throw error;
  
  // Process the results to flatten the structure
  const processedData = (data || []).map(item => ({
    ...item,
    number_account_trading: item.trading_accounts?.number_account_trading,
    csp_account_name: item.api_connections?.csp_accounts?.csp_account_name
  }));
  
  return {
    data: processedData,
    total: count || 0,
    page,
    pageSize
  };
};
